/**
 * Dictado por voz (D-Talk / Mapa de Talento) — funciones PURAS, sin React ni
 * DOM, para poder testearlas desde el backend y usarlas en el front.
 *
 * Existen por un incidente (14/09/2026): una clapper dictó un rato largo, Chrome
 * tiró un error `network` a mitad de camino, y el componente marcó la sesión
 * como cancelada y borró TODO lo acumulado. Además, los segmentos finales se
 * pegaban con `+=` sin garantizar el espacio entre uno y otro.
 */

/**
 * Une segmentos de dictado con UN espacio, sin dobles ni en los bordes.
 *
 * Chrome entrega los `results` finales a veces con espacio inicial (" y
 * después") y a veces sin él; concatenar crudo pegaba palabras. Sirve tanto
 * para acumular finales como para armar lo que ve la barra (finales + interim).
 * Sin texto → '' (la barra muestra el placeholder y `onend` no entrega nada).
 */
export function composeTranscript(...parts: Array<string | null | undefined>): string {
  return parts
    .map((p) => (p ?? '').replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    .join(' ');
}

/**
 * Qué hacer con cada `SpeechRecognitionErrorEvent.error`:
 * - `denied`   — permiso denegado: no hay nada que reintentar; mic bloqueado.
 * - `no_mic`   — no hay dispositivo de captura: entregar lo dictado y cerrar.
 * - `fatal`    — reintentar no lo arregla: entregar lo dictado y cerrar.
 * - `network`  — transitorio: conservar lo dictado y dejar que la sesión se reinicie.
 * - `silent`   — `no-speech`, `aborted` ajeno o un error desconocido: transitorio;
 *                el tope de sesiones vacías acota el loop.
 */
export type SpeechErrorKind = 'denied' | 'no_mic' | 'fatal' | 'network' | 'silent';

export function classifySpeechError(error: string): SpeechErrorKind {
  switch (error) {
    case 'not-allowed':
    case 'service-not-allowed':
      return 'denied';
    case 'audio-capture':
      return 'no_mic';
    case 'network':
      return 'network';
    case 'bad-grammar':
    case 'language-not-supported':
      return 'fatal';
    default:
      return 'silent';
  }
}
